import React, { useEffect, useState } from 'react';
import { FormGroup, Input, Label, Row, Col, Table, Badge } from 'reactstrap';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { fetchAllExamEvents, saveEventConfig } from '../../Services/Employees';
import '../Allocation/Allocation.css';

const formatApiDate = (dateTimeLocal) => {
  if (!dateTimeLocal) return '';
  const [datePart, timePart = '00:00'] = dateTimeLocal.split('T');
  const time = timePart.length === 5 ? `${timePart}:00` : timePart;
  return `${datePart} ${time}`;
};

const toDateTimeLocal = (apiDate) => {
  if (!apiDate) return '';
  return apiDate.replace(' ', 'T').slice(0, 16);
};

const formatDisplayDate = (apiDate) => {
  if (!apiDate) return '-';
  return apiDate.slice(0, 16);
};

const EventConfig = () => {
  const [eventId, setEventId] = useState(null);
  const [event, setEvent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [cutOffDate, setCutOffDate] = useState('');
  const [examinationDate, setExaminationDate] = useState('');
  const [eventActive, setEventActive] = useState(true);
  const [admitCardPublish, setAdmitCardPublish] = useState(false);
  const [resultPublish, setResultPublish] = useState(false);
  const [eventCompleted, setEventCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const loadEvents = () => {
    setLoadingEvents(true);
    fetchAllExamEvents()
      .then((response) => {
        setEvents(response.data.data || []);
      })
      .catch((error) => {
        const message = error.response?.data?.message || 'Unable to load events';
        withReactContent(Swal).fire({ icon: 'error', text: message });
      })
      .finally(() => setLoadingEvents(false));
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const resetForm = () => {
    setEventId(null);
    setEvent('');
    setStartDate('');
    setCutOffDate('');
    setExaminationDate('');
    setEventActive(true);
    setAdmitCardPublish(false);
    setResultPublish(false);
    setEventCompleted(false);
  };

  const handleEdit = (item) => {
    setEventId(item.id);
    setEvent(item.event || '');
    setStartDate(toDateTimeLocal(item.startDate));
    setCutOffDate(toDateTimeLocal(item.cutOffDate));
    setExaminationDate(item.examinationDate || '');
    setEventActive(!!item.eventActive);
    setAdmitCardPublish(!!item.admitCardPublish);
    setResultPublish(!!item.resultPublish);
    setEventCompleted(!!item.eventCompleted);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = () => {
    if (!event.trim()) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please enter event name (e.g. JULY-2026).' });
      return;
    }
    if (!startDate || !cutOffDate) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please select Start Date and Cut-off Date.' });
      return;
    }
    if (!examinationDate) {
      withReactContent(Swal).fire({ icon: 'warning', text: 'Please select Examination Date.' });
      return;
    }

    const payload = {
      event: event.trim().toUpperCase(),
      startDate: formatApiDate(startDate),
      cutOffDate: formatApiDate(cutOffDate),
      examinationDate,
      eventActive,
      admitCardPublish,
      resultPublish,
      eventCompleted,
    };

    if (eventId) {
      payload.id = eventId;
    }

    setLoading(true);

    saveEventConfig(payload)
      .then((response) => {
        const message = typeof response.data === 'string' ? response.data : 'Event saved successfully';
        if (message.toLowerCase().includes('not authorized')) {
          withReactContent(Swal).fire({ icon: 'error', text: message, allowOutsideClick: false });
          return;
        }
        withReactContent(Swal).fire({ icon: 'success', text: message, allowOutsideClick: false });
        resetForm();
        loadEvents();
      })
      .catch((error) => {
        const message = error.response?.data || 'Error saving event';
        withReactContent(Swal).fire({
          icon: 'error',
          text: typeof message === 'string' ? message : 'Error saving event',
          allowOutsideClick: false,
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="card border-0" style={{ background: 'white' }}>
      <h3 style={{ textAlign: 'center' }}>Create / Update Event</h3>

      <div className="excel-upload-container" style={{ marginBottom: '24px', marginTop: '2%', width: '900px', textAlign: 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 className="upload-heading" style={{ marginBottom: 0 }}>
            {eventId ? `Edit Event: ${event}` : 'New Event Configuration'}
          </h2>
          {eventId && (
            <button type="button" className="upload-btn" onClick={resetForm}>
              New Event
            </button>
          )}
        </div>

        <FormGroup>
          <Label for="eventName">Event Name</Label>
          <Input
            id="eventName"
            type="text"
            placeholder="e.g. JULY-2026"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          />
        </FormGroup>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="startDate">Center Selection Start Date</Label>
              <Input
                id="startDate"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="cutOffDate">Center Selection Last Date</Label>
              <Input
                id="cutOffDate"
                type="datetime-local"
                value={cutOffDate}
                onChange={(e) => setCutOffDate(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label for="examinationDate">Examination Date</Label>
          <Input
            id="examinationDate"
            type="date"
            value={examinationDate}
            onChange={(e) => setExaminationDate(e.target.value)}
          />
        </FormGroup>

        <Row className="mb-3">
          <Col md={6}>
            <FormGroup check>
              <Input
                id="eventActive"
                type="checkbox"
                checked={eventActive}
                onChange={(e) => setEventActive(e.target.checked)}
              />
              <Label check for="eventActive" className="ms-2">Event Active</Label>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup check>
              <Input
                id="admitCardPublish"
                type="checkbox"
                checked={admitCardPublish}
                onChange={(e) => setAdmitCardPublish(e.target.checked)}
              />
              <Label check for="admitCardPublish" className="ms-2">Publish Admit Card</Label>
            </FormGroup>
          </Col>
          <Col md={6} className="mt-2">
            <FormGroup check>
              <Input
                id="resultPublish"
                type="checkbox"
                checked={resultPublish}
                onChange={(e) => setResultPublish(e.target.checked)}
              />
              <Label check for="resultPublish" className="ms-2">Publish Result</Label>
            </FormGroup>
          </Col>
          <Col md={6} className="mt-2">
            <FormGroup check>
              <Input
                id="eventCompleted"
                type="checkbox"
                checked={eventCompleted}
                onChange={(e) => setEventCompleted(e.target.checked)}
              />
              <Label check for="eventCompleted" className="ms-2">Event Completed</Label>
            </FormGroup>
          </Col>
        </Row>

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button type="button" className="upload-btn" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : eventId ? 'Update Event' : 'Save Event'}
          </button>
        </div>
      </div>

      <div className="excel-upload-container" style={{ marginBottom: '8%', width: '900px', textAlign: 'left' }}>
        <h2 className="upload-heading" style={{ textAlign: 'center' }}>All Events</h2>
        {loadingEvents ? (
          <p className="text-center">Loading events...</p>
        ) : events.length === 0 ? (
          <p className="text-center">No events found.</p>
        ) : (
          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table bordered responsive hover size="sm">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Start Date</th>
                  <th>Last Date</th>
                  <th>Exam Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {events.map((item) => (
                  <tr key={item.id}>
                    <td>{item.event}</td>
                    <td>{formatDisplayDate(item.startDate)}</td>
                    <td>{formatDisplayDate(item.cutOffDate)}</td>
                    <td>{item.examinationDate || '-'}</td>
                    <td>
                      {item.eventActive && <Badge color="success" className="me-1">Active</Badge>}
                      {item.admitCardPublish && <Badge color="info" className="me-1">Admit Card</Badge>}
                      {item.resultPublish && <Badge color="primary" className="me-1">Result</Badge>}
                      {item.eventCompleted && <Badge color="secondary">Completed</Badge>}
                    </td>
                    <td>
                      <button type="button" className="upload-btn" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventConfig;
